import React from 'react';
import { Box, Typography } from '@mui/material';
import { EmojiEventsOutlined, ForumOutlined, HighlightOffOutlined } from '@mui/icons-material';
import { Cell, Pie, PieChart } from 'recharts';
import { TABLE_FONT_FAMILY } from '../../theme';
import { CommercialDashboard, formatRate } from '../../utils/commercialDashboard';
import { ConsolePanel } from './ConsolePanel';
import { NAUTILUS, useConsolePalette } from './consolePalette';

const DONUT = 132;

type ResultsPanelProps = {
  dashboard: CommercialDashboard;
};

const ResultsPanel: React.FC<ResultsPanelProps> = ({ dashboard }) => {
  const palette = useConsolePalette();
  const { won, lost, other, loss_rate: lossRate } = dashboard.results;
  const total = won + lost + other;

  const items = [
    { key: 'won', label: 'Won', count: won, color: NAUTILUS.sand, ink: palette.wonInk, icon: <EmojiEventsOutlined fontSize="small" /> },
    { key: 'lost', label: 'Lost', count: lost, color: NAUTILUS.buoy, ink: palette.isDark ? '#f0a35e' : '#b35a10', icon: <HighlightOffOutlined fontSize="small" /> },
    { key: 'other', label: 'Other', count: other, color: NAUTILUS.slate, ink: palette.inkSoft, icon: <ForumOutlined fontSize="small" /> },
  ];
  const slices = total > 0
    ? items.filter((item) => item.count > 0).map((item) => ({ key: item.key, value: item.count, color: item.color }))
    : [{ key: 'empty', value: 1, color: palette.inset }];

  return (
    <ConsolePanel
      title="Results"
      subtitle={`Leads closed in ${dashboard.period.label}`}
      icon={<EmojiEventsOutlined />}
      bodySx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(72px, 1fr))', flex: '1 1 260px' }}>
        {items.map((item, index) => (
          <Box
            key={item.key}
            sx={{
              px: 1.5,
              py: 0.5,
              borderLeft: index === 0 ? 'none' : `1px solid ${palette.rule}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: item.ink }}>
              {item.icon}
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'inherit' }}>{item.label}</Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: TABLE_FONT_FAMILY,
                fontWeight: 700,
                fontSize: '1.6rem',
                lineHeight: 1.2,
                color: palette.ink,
                mt: 0.5,
              }}
            >
              {item.count}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: '0 0 auto', mx: 'auto' }}>
        <Box sx={{ position: 'relative', width: DONUT, height: DONUT }} role="img" aria-label={`Loss rate ${formatRate(lossRate)}`}>
          <PieChart width={DONUT} height={DONUT}>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="key"
              innerRadius={46}
              outerRadius={62}
              startAngle={90}
              endAngle={-270}
              stroke="none"
              isAnimationActive={false}
            >
              {slices.map((slice) => (
                <Cell key={slice.key} fill={slice.color} />
              ))}
            </Pie>
          </PieChart>
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
            <Typography
              sx={{ fontFamily: TABLE_FONT_FAMILY, fontWeight: 700, fontSize: '1.3rem', color: palette.figure }}
            >
              {formatRate(lossRate)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ maxWidth: 120 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: palette.ink }}>Loss rate</Typography>
          <Typography variant="caption" sx={{ color: palette.inkFaint }}>
            Lost ÷ (won + lost). Other is left out.
          </Typography>
        </Box>
      </Box>
    </ConsolePanel>
  );
};

export default ResultsPanel;
